import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ModalWindow from '@common/components/modal/modal-window';
import ModalContent from '@common/components/modal/modal-content';
import Icon from '@common/components/icon';
import type { DataTableColumn } from '@common/components/datatable/types';

type EmployeeColumnModalProps = {
  children: React.ReactNode;
  columns: DataTableColumn[];
  visibleKeys: string[];
  onChange: (keys: string[]) => void;
};

const EmployeeColumnModal = memo(
  ({ children, columns, visibleKeys, onChange }: EmployeeColumnModalProps) => {
    const [show, setShow] = useState(false);
    const [tempVisible, setTempVisible] = useState<string[]>(visibleKeys);
    const [orderedColumns, setOrderedColumns] = useState<
      Array<{ key: string; label: string }>
    >(
      columns.map((col) => ({
        key: col.key,
        label: typeof col.label === 'string' ? col.label : col.key,
      }))
    );
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const { t } = useTranslation();

    useEffect(() => {
      if (show) {
        setTempVisible(visibleKeys);
      }
    }, [show, visibleKeys]);

    useEffect(() => {
      setOrderedColumns(
        columns.map((col) => ({
          key: col.key,
          label: typeof col.label === 'string' ? col.label : col.key,
        }))
      );
    }, [columns]);

    const handleHide = () => {
      setShow(false);
    };

    const handleShow = () => {
      setShow(true);
    };

    const alwaysVisibleKey = columns[0]?.key;

    const toggleKey = (key: string) => {
      if (key === alwaysVisibleKey) return;
      setTempVisible((prev) => {
        const set = new Set(prev);
        if (set.has(key)) set.delete(key);
        else set.add(key);
        return Array.from(set);
      });
    };

    const isVisible = (key: string) => tempVisible.includes(key);

    const handleDragStart = (index: number) => {
      if (index === 0) return;
      setDraggedIndex(index);
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
      e.preventDefault();
      if (index === 0 || draggedIndex === null || draggedIndex === index)
        return;

      const newColumns = [...orderedColumns];
      const draggedItem = newColumns[draggedIndex];
      newColumns.splice(draggedIndex, 1);
      newColumns.splice(index, 0, draggedItem);

      setOrderedColumns(newColumns);
      setDraggedIndex(index);
    };

    const handleDragEnd = () => {
      setDraggedIndex(null);
    };

    const handleConfirm = () => {
      const orderedVisibleKeys = orderedColumns
        .map((col) => col.key)
        .filter((key) => tempVisible.includes(key));
      onChange(orderedVisibleKeys);
      handleHide();
    };

    return (
      <ModalWindow
        trigger={children}
        show={show}
        onHide={handleHide}
        onShow={handleShow}
        size="small"
      >
        <ModalContent
          title={t('performance:employee_column')}
          onHide={handleHide}
          onConfirm={handleConfirm}
          confirmButtonText={t('common:save') || 'Save'}
        >
          <div className="tw-flex tw-flex-col tw-gap-2">
            {orderedColumns.map((item, index) => (
              <div
                key={item.key}
                draggable={index !== 0}
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={`tw-flex tw-flex-row tw-justify-between tw-items-center tw-py-2 tw-px-2 tw-rounded ${
                  index !== 0 ? 'tw-cursor-move' : ''
                } ${draggedIndex === index ? 'tw-bg-gray-100' : ''} ${
                  index !== 0 ? 'hover:tw-bg-gray-50' : ''
                }`}
                style={{
                  transition: draggedIndex === index ? 'none' : 'all 0.2s ease',
                }}
              >
                <div className="tw-flex tw-flex-row tw-gap-2 tw-items-center">
                  <Icon
                    type="dragable_six_dots"
                    className={`tw-text-gray-textGray !tw-h-4 !tw-w-4 ${
                      index === 0
                        ? 'tw-opacity-40'
                        : 'tw-cursor-grab active:tw-cursor-grabbing'
                    }`}
                  />
                  <div className="tw-text-gray-800 tw-text-sm-plus tw-font-medium">
                    {item.label}
                  </div>
                </div>
                <Icon
                  type={isVisible(item.key) ? 'eye_on' : 'eye_off'}
                  color="grey"
                  className={`tw-cursor-pointer !tw-h-4 !tw-w-4 ${
                    item.key === alwaysVisibleKey
                      ? 'tw-opacity-40 tw-cursor-not-allowed'
                      : ''
                  }`}
                  onClick={() => toggleKey(item.key)}
                />
              </div>
            ))}
          </div>
        </ModalContent>
      </ModalWindow>
    );
  }
);

export default EmployeeColumnModal;
